import React, { useCallback, useState } from "react";
import { IssueTypeChangeModal } from "./IssueTypeChangeModal";
import { DefaultEditFieldInterface } from "./typesForFields";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";
import { useGetUnfilledAndRequiredIssueFieldsMetadata } from "../../../utils/hooks/requiredFieldsByTypeHooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { Issue, IssueStatus } from "@adsk/acc-issues-types";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { TypeField } from "../../../utils/components/Fields/TypeField";
import { useFieldTestId } from "../../../utils/components/IssueFieldPresentationContext";

export const DetailPanelTypeField = ({
  issue,
  onChange,
  containerRef,
  viewOnly,
}: DefaultEditFieldInterface) => {
  const testId = useFieldTestId("type-field");
  const isDisabled = viewOnly || !isAttributeEditable(issue, "issueTypeId");
  const [pendingTypeId, setPendingTypeId] = useState<string>();
  const getUnfilledAndRequiredIssueFieldsMetadata =
    useGetUnfilledAndRequiredIssueFieldsMetadata();
  const { T } = useTranslations();
  const handleChange = useCallback(
    ({
      typeId,
      inlineEditOnChange,
    }: {
      typeId: string;
      inlineEditOnChange: (arg0: string | undefined) => void;
    }) => {
      const requiredIssueFields = getUnfilledAndRequiredIssueFieldsMetadata({
        issueTypeId: typeId,
        issue,
      });
      if (issue.status !== IssueStatus.Draft && requiredIssueFields.length) {
        setPendingTypeId(typeId);
      } else {
        inlineEditOnChange(typeId);
        onChange({ issueTypeId: typeId });
      }
    },
    [getUnfilledAndRequiredIssueFieldsMetadata, issue, onChange]
  );

  return (
    <div data-testid={testId}>
      <TypeField
        status={issue.status}
        typeId={issue.issueTypeId}
        typeCategoryId={issue.issueTypeCategoryId}
        label={T.translate("issueAttributes.issueType") as string}
        onChange={handleChange}
        disabled={isDisabled}
        containerRef={containerRef}
        showDisabledReason={!viewOnly}
      />
      <IssueTypeChangeModal
        pendingTypeId={pendingTypeId || ""}
        requiredFieldsMetadata={getUnfilledAndRequiredIssueFieldsMetadata({
          issueTypeId: pendingTypeId,
          issue,
        })}
        onComplete={(issueChanges: Partial<IssueFormAttributes>) => {
          // our types need a refactoring, see `IssueDetailTabPanelBody` for more detail.
          onChange(issueChanges as Partial<Issue>);
          setPendingTypeId(undefined);
        }}
        onClose={() => setPendingTypeId(undefined)}
      />
    </div>
  );
};
